import isEqual from "lodash/isEqual";

import type {
  Position,
  PositionBasePreset,
  PositionTransformation,
} from "@/shared/state/visualization/schema/v1/three-d";

import { bindingFor, type TopicDataPathBinding } from "./positionTopicData";

export interface PositionPreset {
  /** The data used to identify and present this preset in a list of presets. */
  name: {
    title: string;
    subtitle: string;
    /** A hint at what file formats commonly use this preset. */
    formatHint: string;
  };

  /**
   * The original type of preset this preset is based on.
   *
   * Should not be changed when duplicating a preset.
   */
  basePreset: PositionBasePreset;

  /**
   * The coordinate transformation necessary to render data from this preset in the 3D panel.
   *
   * Should not be changed when duplicating a preset.
   */
  transformation: PositionTransformation;

  /**
   * The semantic labels to show for each axis. (Lat/Lon/Alt, East/North/Up, etc).
   *
   * Should not be changed when duplicating a preset.
   */
  labels: {
    forward: string;
    right: string;
    up: string;
  };

  /**
   * Multiplied into each axis value when the 3D panel renders it. A negative
   * scale flips that axis: `north_east_down` carries `up: -1` so a
   * down-positive measurement plots upward in the scene.
   */
  scales: {
    forward: number;
    right: number;
    up: number;
  };

  /**
   * The topic data each axis plots. The presets in {@link presets} carry none,
   * describing a coordinate convention alone.
   */
  bindings?: {
    forward?: TopicDataPathBinding;
    right?: TopicDataPathBinding;
    up?: TopicDataPathBinding;
  };
}

/**
 * The preset describing a position already stored on an entity, so a position
 * the user configured by hand is selectable in the preset list. Undefined when
 * there is no position, or the position names no base preset.
 *
 * 1. The base preset itself, when the position keeps that preset's scales and
 *    binds no axis to topic data.
 * 2. Otherwise a copy of the base preset, titled "Custom" plus the base
 *    preset's title, carrying the position's own scales and per-axis bindings.
 */
export function presetFor(
  position: Position | undefined,
): PositionPreset | undefined {
  if (!position || !position.basePreset) {
    return undefined;
  }
  const basePreset = presets[position.basePreset];
  const scales = {
    forward: position.forwardScale,
    right: position.rightScale,
    up: position.upScale,
  };

  const forwardConfig = bindingFor(position.forward);
  const rightConfig = bindingFor(position.right);
  const upConfig = bindingFor(position.up);

  if (
    isEqual(scales, basePreset.scales) &&
    !forwardConfig &&
    !rightConfig &&
    !upConfig
  ) {
    return basePreset;
  }

  return {
    ...basePreset,
    name: {
      ...basePreset.name,
      title: `Custom (${basePreset.name.title})`,
      formatHint: "",
    },
    scales,
    bindings: {
      forward: forwardConfig,
      right: rightConfig,
      up: upConfig,
    },
  };
}

const baseLatLonAltPreset: PositionPreset = {
  name: {
    title: "Lat/Lon/Alt",
    subtitle: "deg/deg/m",
    formatHint: "Common in PX4/ROS",
  },
  labels: {
    right: "Lat",
    forward: "Lon",
    up: "Alt",
  },
  scales: {
    right: 1,
    forward: 1,
    up: 1,
  },
  transformation: "lla_to_enu",
  basePreset: "lat_lon_alt",
};

const baseEastNorthUpPreset: PositionPreset = {
  name: {
    title: "East/North/Up",
    subtitle: "m/m/m",
    formatHint: "Common in ROS",
  },
  labels: {
    right: "East",
    forward: "North",
    up: "Up",
  },
  scales: {
    right: 1,
    forward: 1,
    up: 1,
  },
  transformation: null,
  basePreset: "east_north_up",
};

const baseNorthEastDownPreset: PositionPreset = {
  name: {
    title: "North/East/Down",
    subtitle: "m/m/-m",
    formatHint: "Common in PX4",
  },
  labels: {
    right: "East",
    forward: "North",
    up: "Down",
  },
  scales: {
    right: 1,
    forward: 1,
    up: -1,
  },
  transformation: null,
  basePreset: "north_east_down",
};

const baseForwardRightUpPreset: PositionPreset = {
  name: {
    title: "Forward/Right/Up",
    subtitle: "m/m/m",
    formatHint: "Common in ROS",
  },
  labels: {
    forward: "Forward",
    right: "Right",
    up: "Up",
  },
  scales: {
    forward: 1,
    right: 1,
    up: 1,
  },
  transformation: null,
  basePreset: "forward_right_up",
};

export const presets: Record<PositionBasePreset, PositionPreset> = {
  lat_lon_alt: baseLatLonAltPreset,
  east_north_up: baseEastNorthUpPreset,
  north_east_down: baseNorthEastDownPreset,
  forward_right_up: baseForwardRightUpPreset,
};
